import axios from "axios";
import { ensureStorefrontCompanyContext, getTextLocale } from "lib/storefront";
import { cacheCompanyProfiles } from "lib/companyProfile";
import {
  getStorefrontPreviewCompanyKey,
  getStorefrontPreviewJwt,
  isStorefrontPreviewActive,
} from "lib/storefrontPreview";

function normalizeCompanyKey(value) {
  const key = String(value || "")
    .trim()
    .toLowerCase();
  return /^[a-z0-9][a-z0-9_-]{0,63}$/.test(key) ? key : "";
}

// Determine API base URL
let computedBase = process.env.REACT_APP_API_URL;
if (!computedBase && typeof window !== "undefined") {
  const isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  if (isLocal) {
    computedBase = "http://localhost:1337";
  } else {
    // Use same domain (nginx proxies /api/* to backend on port 1337)
    // Frontend API calls already include /api in the path, so baseURL is just the domain
    computedBase = `${window.location.protocol}//${window.location.hostname}`;
  }
}
const baseURL = computedBase || "http://localhost:1337";

function isTenantLockedStorefrontRequest(url) {
  const path = String(url || "");
  if (!path.startsWith("/api/storefront/")) return false;
  if (path.startsWith("/api/storefront/staff/")) return false;
  if (path.includes("/staff")) return false;
  return path !== "/api/storefront/config";
}

function isStorefrontConfigRequest(url) {
  return String(url || "") === "/api/storefront/config";
}

function cacheCompanyProfilesFromResponse(body) {
  if (!body || typeof body !== "object") return;
  const rows = [];
  if (body.meta?.company) rows.push(body.meta.company);
  if (body.data?.company) rows.push(body.data.company);
  if (Array.isArray(body.companyBranding)) rows.push(...body.companyBranding);
  if (Array.isArray(body.meta?.companyBranding))
    rows.push(...body.meta.companyBranding);
  if (rows.length) cacheCompanyProfiles(rows);
}

export const api = axios.create({
  baseURL,
  timeout: 30000, // 30 second timeout
  headers: {},
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const previewMode = isStorefrontPreviewActive();
    const previewJwt = previewMode ? getStorefrontPreviewJwt() : "";
    const token = previewJwt || localStorage.getItem("jwt");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Company context (multi-company support)
    const storedCompany = normalizeCompanyKey(
      localStorage.getItem("activeCompany")
    );
    const previewCompany = previewMode ? getStorefrontPreviewCompanyKey() : "";
    const storefrontCompany =
      previewCompany || ensureStorefrontCompanyContext();
    const activeCompany = isStorefrontConfigRequest(config.url)
      ? ""
      : isTenantLockedStorefrontRequest(config.url)
        ? storefrontCompany || storedCompany || previewCompany
        : storedCompany || previewCompany || storefrontCompany || "";
    // Allow per-request overrides (e.g. cross-company read-only views)
    const alreadyHasCompanyHeader = !!(
      config.headers &&
      (config.headers["X-VBS-Company"] || config.headers["x-vbs-company"])
    );
    if (activeCompany && !alreadyHasCompanyHeader) {
      config.headers = config.headers || {};
      config.headers["X-VBS-Company"] = activeCompany;
    }
    config.headers = config.headers || {};
    if (previewMode && isTenantLockedStorefrontRequest(config.url)) {
      config.headers["X-Storefront-Preview"] = "staff";
    }
    if (!config.headers["Accept-Language"]) {
      config.headers["Accept-Language"] = getTextLocale();
    }
    // Ensure multipart/form-data is used when sending FormData
    if (config.data instanceof FormData) {
      if (config.headers && config.headers["Content-Type"]) {
        delete config.headers["Content-Type"]; // Let the browser set the boundary
      }
    } else {
      // Default JSON content-type for non-FormData payloads
      config.headers = config.headers || {};
      if (!config.headers["Content-Type"])
        config.headers["Content-Type"] = "application/json";
    }
    // Log request in development
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
        {
          params: config.params,
          data: config.data,
        }
      );
    }
    return config;
  },
  (error) => {
    console.error("[API Request Error]", error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (res) => {
    cacheCompanyProfilesFromResponse(res?.data);
    // Log successful response in development
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[API Response] ${res.config.method?.toUpperCase()} ${res.config.url}`,
        {
          status: res.status,
          data: res.data,
        }
      );
    }
    return res;
  },
  (err) => {
    // Enhanced error logging
    const errorDetails = {
      message: err.message,
      url: err.config?.url,
      method: err.config?.method,
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
    };

    if (err?.response?.status === 401) {
      // Clear token immediately on 401
      localStorage.removeItem("jwt");
      // Dispatch custom event to notify AuthContext
      window.dispatchEvent(new CustomEvent("auth-token-cleared"));
      console.warn("[API] Unauthorized - Token cleared");
    } else if (err?.response?.status === 404) {
      console.error("[API] Not Found", errorDetails);
    } else if (err?.response?.status >= 500) {
      console.error("[API] Server Error", errorDetails);
    } else if (err.code === "ECONNABORTED") {
      console.error("[API] Request Timeout", errorDetails);
    } else if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
      console.error(
        "[API] Network Error - Check if backend is running at",
        baseURL,
        errorDetails
      );
    } else {
      console.error("[API Error]", errorDetails);
    }

    return Promise.reject(err);
  }
);

export async function get(path, paramsOrConfig) {
  try {
    const config =
      paramsOrConfig &&
      typeof paramsOrConfig === "object" &&
      (paramsOrConfig.params ||
        paramsOrConfig.headers ||
        paramsOrConfig.responseType)
        ? paramsOrConfig
        : { params: paramsOrConfig };
    const res = await api.get(path, config);
    return res.data;
  } catch (error) {
    throw error;
  }
}

// Fetch all pages from a Strapi collection endpoint (REST pagination).
// Uses the first request to discover total pages, then loads the rest in small parallel batches.
export async function getAllPages(path, params = {}, options = {}) {
  const pageSize = Math.max(1, Number(options.pageSize || 100));
  const concurrency = Math.max(1, Number(options.concurrency || 5));

  const first = await get(path, {
    ...(params || {}),
    pagination: { page: 1, pageSize },
  });

  const total = first?.meta?.pagination?.total ?? 0;
  const totalPages =
    first?.meta?.pagination?.pageCount ??
    (total ? Math.ceil(total / pageSize) : 1);

  const allData = [...(first?.data || [])];

  if (totalPages <= 1) {
    return { data: allData, meta: first?.meta };
  }

  // Fetch remaining pages (2..N) in batches to avoid hammering the API.
  let page = 2;
  while (page <= totalPages) {
    const batch = [];
    for (let i = 0; i < concurrency && page <= totalPages; i += 1, page += 1) {
      batch.push(
        get(path, {
          ...(params || {}),
          pagination: { page, pageSize },
        })
      );
    }
    const results = await Promise.all(batch);
    results.forEach((r) => {
      allData.push(...(r?.data || []));
    });
  }

  return { data: allData, meta: first?.meta };
}

export async function post(path, body, params) {
  try {
    const res = await api.post(path, body, { params });
    return res.data;
  } catch (error) {
    throw error;
  }
}

export async function put(path, body, params) {
  try {
    const res = await api.put(path, body, { params });
    return res.data;
  } catch (error) {
    throw error;
  }
}

export async function del(path, params) {
  try {
    const res = await api.delete(path, { params });
    return res.data;
  } catch (error) {
    throw error;
  }
}
